/**
 * API-клієнт (Sprint 8).
 *
 * Конфігурація:
 * - baseURL з VITE_API_URL (production-білд Cloudflare Pages), fallback '' —
 *   Vite proxy (vite.config.ts) перенаправляє /api → 8000 у dev.
 *   У dev лишай VITE_API_URL порожнім (або .env.local не створюй),
 *   у prod-білді задаєш `VITE_API_URL=https://api.workflow81.com`.
 * - withCredentials: false — Bearer-токени, не cookies.
 *
 * Interceptors:
 * 1. REQUEST: інжекція `Authorization: Bearer <access>` з useAuthStore.
 *    Якщо токена немає — запит проходить без header-а (бекенд відповість
 *    401, який ми обробимо нижче).
 *
 * 2. RESPONSE 401: спроба rotate через /api/v1/auth/refresh:
 *    - Single-flight: одночасні 401 не плодять N refresh-запитів —
 *      перший стартує, інші чекають його promise.
 *    - Якщо refresh ОК: оновлюємо store + retry оригінальний запит.
 *    - Якщо refresh fail: logout + toast + редирект /login.
 *    - Запити НА сам /auth/refresh не retry-имо (інакше — нескінченний цикл).
 *
 * 3. RESPONSE 403: toast "немає прав" + reject (caller сам вирішить
 *    чи показувати fallback-UI). ЖОДНОГО logout: 403 про стан зміни
 *    (shift_closed / shift_closed_today) лишає людину на екрані.
 *
 * 4. RESPONSE 5xx / network: toast "сервер недоступний".
 *
 * Усі error-message-и беремо з error-envelope (Sprint 2: { error: {code,
 * message, details} }). Якщо envelope-у немає (offline, CORS-fail) —
 * показуємо generic-меседж.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/store/auth";
import { toast } from "@/store/toast";
import { errorCode, errorMessage, fieldErrors } from "@/lib/apiErrors";
import { clearStale, loadRead, saveRead } from "@/lib/offlineReads";
import type { TokenPair } from "@/types/api";

/** Кастомні поля у request config — для single-flight refresh. */
interface RetryFlags {
  _retry?: boolean;             // вже робили refresh у цьому ланцюгу
  _skipAuth?: boolean;          // запит на /auth/* — не інжектити Bearer
  _silent?: boolean;            // не показувати глобальні тости (напр. сторінка друку)
  // 403 тут ОЧІКУВАНИЙ: необовʼязкова секція екрана, якої в цієї ролі просто
  // немає (брак ОТК у картці замовлення для конструктора). Решта помилок
  // (500, мережа, 422) лишаються голосними — на відміну від `_silent`.
  // Куплено 03.10.2026: Петельчук відкривала замовлення і бачила
  // «Немає прав на цю дію», хоча нічого не робила й переглядати могла.
  _quiet403?: boolean;
}

/** Конфіг для необовʼязкової секції: 403 = «секції в цієї ролі немає». */
export const QUIET_403 = { _quiet403: true } as unknown as AxiosRequestConfig;

type AxiosReqCfgEx = InternalAxiosRequestConfig & RetryFlags;

export const apiClient: AxiosInstance = axios.create({
  // dev: '' → Vite proxy переадресовує /api → :8000.
  // prod: VITE_API_URL виставляється у Cloudflare Pages env (наприклад
  // 'https://api.workflow81.com'). Слеш у кінці не потрібен.
  baseURL: import.meta.env.VITE_API_URL ?? "",
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------- Request interceptor: Bearer injection ----------

apiClient.interceptors.request.use((cfg) => {
  const c = cfg as AxiosReqCfgEx;
  if (c._skipAuth) return cfg;
  const token = useAuthStore.getState().accessToken;
  if (token) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// ---------- Single-flight refresh ----------

let _refreshPromise: Promise<string | null> | null = null;

/** Виконує /api/v1/auth/refresh і повертає новий access-token (або null). */
async function performRefresh(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    // ВАЖЛИВО: baseURL той самий, що в apiClient (VITE_API_URL). Без нього у
    // проді refresh летів на origin фронта (workflow81.com/api/... → 404 на
    // статиці Cloudflare) замість api.workflow81.com → refresh завжди падав і
    // юзера викидало щогодини / при перезапуску браузера.
    const resp = await axios.post<TokenPair>(
      `${import.meta.env.VITE_API_URL ?? ""}/api/v1/auth/refresh`,
      { refresh_token: refresh },
      // Окремий axios (не apiClient), щоб не потрапити у свій же 401-interceptor.
    );
    useAuthStore.getState().updateTokens(resp.data);
    return resp.data.access_token;
  } catch {
    return null;
  }
}

/** Гарантує що в момент часу йде максимум один refresh — інші чекають. */
function refreshOnce(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = performRefresh().finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

// ---------- Helpers ----------

function isAuthRefreshUrl(url: string | undefined): boolean {
  return Boolean(url && url.includes("/api/v1/auth/refresh"));
}

function isLoginUrl(url: string | undefined): boolean {
  return Boolean(url && url.includes("/api/v1/auth/login/"));
}

// ---------- Response interceptor ----------

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const cfg = error.config as AxiosReqCfgEx | undefined;
    const status = error.response?.status;
    const silent = cfg?._silent === true;   // сторінка друку тощо — без тостів

    // Відповіді немає. Причин ДВІ, і плутати їх дорого:
    //   1. у пристрою справді немає мережі;
    //   2. сервер відповів, але браузер не дав відповідь прочитати
    //      (найчастіше — 500 без CORS-заголовків) або обірвав звʼязок.
    //
    // Доти в обох випадках писали «Сервер недоступний. Перевір зʼєднання» —
    // і людина йшла перевіряти інтернет. Куплено 04.09.2026: менеджер шість
    // днів не міг завести замовлення з 1С, бо в роуті лежав NameError, а
    // повідомлення весь цей час показувало пальцем на мережу.
    if (!error.response) {
      // ЧИТАННЯ мовчить тут навмисно. Далі `apiGet` спробує віддати останні
      // збережені дані, і тоді повідомляти нема про що — над екраном зʼявиться
      // смуга «дані станом на 14:32». А обіцянка «дія збережеться й піде» до
      // читання не стосується взагалі: нічого не зберігається. Якщо кешу не
      // виявиться, `apiGet` скаже про це сам.
      const isGet = (cfg?.method ?? "get").toLowerCase() === "get";
      if (!silent && !isGet) {
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        toast.error(
          offline
            ? "Немає інтернету на пристрої. Дія збережеться й піде, щойно звʼязок повернеться."
            : "Сервер не відповів на цей запит. Інтернет у вас є — покажіть цей екран "
              + "адміністратору, причина в системі.",
        );
      }
      return Promise.reject(error);
    }

    // ----- 401: auto-refresh + retry -----
    if (status === 401 && cfg && !cfg._retry && !isAuthRefreshUrl(cfg.url) && !isLoginUrl(cfg.url)) {
      const newAccess = await refreshOnce();
      if (newAccess) {
        cfg._retry = true;
        cfg.headers = cfg.headers ?? {};
        cfg.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient.request(cfg);
      }
      // Refresh fail → повний logout + редирект.
      useAuthStore.getState().logout();
      toast.error("Сесія завершена, увійдіть знову.");
      // Без import { useNavigate } — ми поза React-ом. Простіше:
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // ----- 403: немає прав -----
    if (status === 403) {
      const code = errorCode(error);

      // Стан зміни — НЕ привід розлогінювати. Обидва коди означають лише
      // «зараз зміни немає», а не «ця сесія недійсна»:
      //   shift_closed       — зміну на сьогодні ще не відкрито;
      //   shift_closed_today — день уже завершено (сама, бо системне
      //                        автозакриття бек тепер пускає назад).
      // Причину показує сам екран — смуга зміни у кабінеті / екран входу;
      // тост тут лише дублює її, поки людина дивиться в інше місце.
      //
      // Куплено болем: планувальник о 19:00 закривав зміну заднім числом на
      // 18:00, поки швея ще шила. Наступна дія давала 403 shift_closed_today,
      // і цей перехоплювач мовчки робив logout + редирект на /login, де
      // єдина кнопка «Увійти» давала той самий 403. Людина бачила тільки те,
      // що її викинуло, і пояснення не було ніде.
      if (code === "shift_closed" || code === "shift_closed_today") {
        if (!silent) {
          toast.warning(
            errorMessage(
              error,
              code === "shift_closed"
                ? "Зміну ще не відкрито. Натисніть «Почати робочий день» угорі екрана."
                : "Робочий день уже завершено. Попросіть майстра відкрити його знову.",
            ),
          );
        }
        return Promise.reject(error);
      }
      if (!silent && !cfg?._quiet403) toast.error(errorMessage(error, "Немає прав для цієї дії."));
      return Promise.reject(error);
    }

    // ----- 422 -----
    //
    // C8. Тут довго стояло «не показуємо toast, форма сама обробить» — і саме
    // це робило 80 порожніх `catch` мовчазними. Але 422 буває ДВОХ різних
    // сортів, і плутати їх дорого:
    //
    //   1. розбір ПОЛІВ (pydantic): `error.details.errors` зі списком полів.
    //      Його справді малює сама форма — підсвічує поле, пише «Потрібне
    //      число». Тост тут лише дублює те, що людина вже бачить;
    //
    //   2. відмова за ПРАВИЛОМ: `HTTPException(422, "Цей цикл уже видано…")`.
    //      Полів немає, малювати нема де — і поки перехоплювач мовчав, ця
    //      відмова не доходила НІКУДИ. Людина тиснула кнопку, нічого не
    //      відбувалось, і причину дізнатись не було як.
    //
    // Тепер мовчимо лише на першому сорті. Куплено рівно цим: «Зберегти» без
    // жодної реакції — це та сама поломка, з якої почались правила проєкту.
    if (status === 422) {
      const hasFieldErrors = Object.keys(fieldErrors(error)).length > 0;
      if (!silent && !hasFieldErrors) {
        toast.warning(errorMessage(error, "Дію не прийнято. Перевірте введене."));
      }
      return Promise.reject(error);
    }

    // ----- 429: rate-limit -----
    if (status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      if (!silent) toast.warning(
        `Забагато запитів. Спробуй через ${retryAfter ?? "хвилину"}.`,
      );
      return Promise.reject(error);
    }

    // ----- 5xx / інші -----
    if (!silent) {
      if (status !== undefined && status >= 500) {
        toast.error("Помилка сервера. Спробуй пізніше.");
      } else {
        // 4xx без явного хендлера — теж показуємо envelope-меседж
        toast.warning(errorMessage(error, "Не вдалось виконати дію."));
      }
    }
    return Promise.reject(error);
  },
);

// ---------- Convenience-обгортка ----------

/** Типобезпечний GET — повертає `data` з envelope, а не сирий response. */
export async function apiGet<T>(url: string, cfg?: AxiosRequestConfig): Promise<T> {
  try {
    const r = await apiClient.get<{ data: T }>(url, cfg);
    // Останній вдалий відповідь лишаємо на випадок, коли мережа зникне.
    void saveRead(url, r.data.data);
    clearStale();
    return r.data.data;
  } catch (e) {
    // Кешем підмінюємо ЛИШЕ справжню відсутність мережі. Помилка сервера має
    // лишитись помилкою: 500, підмінений вчорашніми даними, — це поломка, яка
    // сховалась, і шукати її потім нема за чим.
    const err = e as { response?: unknown };
    // Умова — ВІДСУТНІСТЬ ВІДПОВІДІ, а не `navigator.onLine`.
    //
    // `onLine` каже лише «мережевий інтерфейс піднятий»: у цеху, під'єднаному
    // до точки доступу без інтернету, або коли ліг тунель, він лишається
    // `true` — тобто саме в тих випадках, заради яких кеш і зроблено, він би
    // не спрацював жодного разу. Черга запису (`offlineQueue`) вирішує це
    // питання так само — `!err.response`, — і читання не має відповідати
    // інакше на ту саму подію.
    const noAnswer = !err.response;
    if (noAnswer) {
      const cached = await loadRead<T>(url);
      if (cached) return cached.data;
      // Кешу немає — тепер сказати треба, бо перехоплювач для читань мовчить.
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      toast.error(
        offline
          ? "Немає інтернету, і збережених даних для цього екрана теж немає."
          : "Сервер не відповів. Інтернет у вас є — покажіть цей екран адміністратору.",
      );
    }
    throw e;
  }
}

/** GET без envelope — для роутів, що віддають тіло напряму (напр. /auth/me). */
export async function apiGetRaw<T>(url: string, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.get<T>(url, cfg);
  return r.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  cfg?: AxiosRequestConfig,
): Promise<T> {
  const r = await apiClient.post<{ data: T }>(url, body, cfg);
  return r.data.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  cfg?: AxiosRequestConfig,
): Promise<T> {
  const r = await apiClient.patch<{ data: T }>(url, body, cfg);
  return r.data.data;
}

export async function apiDelete<T = unknown>(url: string, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.delete<{ data: T }>(url, cfg);
  return r.data?.data as T;
}

/** Логін НЕ обгортає response у { data }, тому окрема helper. */
export async function apiLoginRaw<T>(url: string, body: unknown): Promise<T> {
  // _skipAuth — щоб не інжектити старий протермінований Bearer.
  // cfg тут — звичайний AxiosRequestConfig + наш кастомний прапор; кастимо
  // через RetryFlags, щоб TS не репортив unknown-property.
  const cfg: AxiosRequestConfig & RetryFlags = {
    headers: { "Content-Type": "application/json" },
    _skipAuth: true,
  };
  const r = await apiClient.post<T>(url, body, cfg);
  return r.data;
}

// Скільки запитів чекає відповіді. Сторож версії не перезавантажує сторінку,
// поки тут не нуль: інакше «ЗАВЕРШИТИ» на повільній мережі обірвалось би
// ДО того, як дія потрапила б або на сервер, або в чергу (код-ревю 15.10.2026).
let inflight = 0;
export const hasInflight = (): boolean => inflight > 0;
apiClient.interceptors.request.use((cfg) => {
  inflight += 1;
  return cfg;
});
apiClient.interceptors.response.use(
  (r) => {
    inflight = Math.max(0, inflight - 1);
    return r;
  },
  (e) => {
    inflight = Math.max(0, inflight - 1);
    return Promise.reject(e);
  },
);
